import ArrowBack from "@mui/icons-material/ArrowBack";
import ArrowDownward from "@mui/icons-material/ArrowDownward";
import ArrowForward from "@mui/icons-material/ArrowForward";
import ArrowUpward from "@mui/icons-material/ArrowUpward";
import { ToggleButton, ToggleButtonGroup } from "@mui/material";
import { styled } from "@mui/material/styles";
import * as React from "react";

const StyledToggleButtonGroup = styled(ToggleButtonGroup)(({ theme }) => ({
  "& .MuiToggleButton-root": {
    padding: "4px 8px",
    border: `1px solid ${theme.palette.divider}`,
    "&.Mui-selected": {
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.primary.contrastText,
      "&:hover": {
        backgroundColor: theme.palette.primary.dark,
      },
    },
  },
}));

export type Direction = "up" | "right" | "down" | "left" | null;

type Props = {
  value: Direction;
  onChange: (direction: Direction) => void;
};

/**
 * Converts a direction to degrees for use in Position.angle
 * Up/North = 0°, Right/East = 90°, Down/South = 180°, Left/West = 270°
 */
export const directionToDegrees = (direction: Direction): number | null => {
  switch (direction) {
    case "up":
      return 0;
    case "right":
      return 90;
    case "down":
      return 180;
    case "left":
      return 270;
    default:
      return null;
  }
};

/**
 * Converts degrees to the nearest cardinal direction
 */
export const degreesToDirection = (angle: number | null): Direction => {
  if (angle === null) return null;
  // Normalize to 0-360 range
  const normalized = ((angle % 360) + 360) % 360;
  // Round to nearest cardinal direction
  if (normalized < 45 || normalized >= 315) return "up";
  if (normalized < 135) return "right";
  if (normalized < 225) return "down";
  return "left";
};

export const DirectionSelector: React.FC<Props> = ({ value, onChange }) => {
  const handleChange = (
    _event: React.MouseEvent<HTMLElement>,
    newDirection: Direction
  ) => {
    onChange(newDirection);
  };

  return (
    <StyledToggleButtonGroup
      value={value}
      exclusive
      onChange={handleChange}
      size="small"
    >
      <ToggleButton value="up" aria-label="up">
        <ArrowUpward fontSize="small" />
      </ToggleButton>
      <ToggleButton value="right" aria-label="right">
        <ArrowForward fontSize="small" />
      </ToggleButton>
      <ToggleButton value="down" aria-label="down">
        <ArrowDownward fontSize="small" />
      </ToggleButton>
      <ToggleButton value="left" aria-label="left">
        <ArrowBack fontSize="small" />
      </ToggleButton>
    </StyledToggleButtonGroup>
  );
};
